import { useEffect, useState } from "react";
import { Truck, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fmtDateMtl } from "../../lib/dateMtl";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

// Étiquette d'expédition STALLION EXPRESS (demande du propriétaire) — bouton
// réutilisable sur une commande boutique OU une soumission payée : crée
// l'expédition + l'étiquette PDF, puis affiche le suivi automatique du colis
// (webhook Stallion → statut à jour, « Livré » coché tout seul).
// Tant que la clé API ou le code postal de l'atelier manque, le bouton est grisé.
export const StallionLabel = ({ kind, docId, prefill = {}, stallion, onSaved,
                                prixClient = null, onPrixClient = null }) => {
  const [cfg, setCfg] = useState(null);   // {configured, reason, env}
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);
  const [est, setEst] = useState(null);  // estimation prix/délai SANS achat (popup)
  const [prixClientLocal, setPrixClientLocal] = useState("");  // livraison facturée au client (ajustable)

  useEffect(() => {
    api.get("/admin/stallion/status").then(({ data }) => setCfg(data))
      .catch(() => setCfg({ configured: false, reason: "Statut Stallion indisponible" }));
  }, []);

  // Estimation de livraison (demande du propriétaire 2026-10-10) : dès l'ouverture
  // de la popup, on interroge Stallion SANS acheter — service le moins cher avec
  // suivi, prix approximatif et délai en jours
  const estimer = async (forme) => {
    setEst({ chargement: true });
    try {
      const { data } = await api.post("/admin/stallion/estimate", { kind, doc_id: docId, ...forme });
      setEst(data);
    } catch {
      setEst(null);
    }
  };

  const begin = () => {
    const forme = {
      name: prefill.name || "", address1: prefill.address1 || "", address2: prefill.address2 || "",
      city: prefill.city || "", province_code: prefill.province || "QC",
      postal_code: prefill.postal_code || "", phone: prefill.phone || "",
      email: prefill.email || "", weight: 2, length: 12, width: 9, height: 6,
    };
    setF(forme);
    setPrixClientLocal(prixClient != null ? String(Number(prixClient).toFixed(2)) : "");
    setOpen(true);
    if (forme.address1 && forme.city && forme.postal_code) estimer(forme);
    else setEst(null);
  };

  const create = async () => {
    setBusy(true);
    try {
      // Prix de livraison client ajusté (demande du propriétaire 2026-10-10) :
      // d'abord la soumission (si modifiable), puis l'achat de l'étiquette
      const pv = prixClientLocal.trim() === "" ? null : Math.max(0, Number(prixClientLocal) || 0);
      if (onPrixClient && pv != null && pv !== prixClient) {
        await onPrixClient(pv);
      }
      const { data } = await api.post("/admin/stallion/labels", { kind, doc_id: docId, ...f, prix_client: pv });
      toast.success(`Étiquette créée — suivi ${data.tracking_number}. Le colis se suivra tout seul.`);
      onSaved({ ...(stallion || {}), tracking_number: data.tracking_number, label_url: data.label_url,
                label_file: data.label_url, status: "created", service: data.service, cost: data.cost,
                prix_client: prixClientLocal.trim() === "" ? null : Math.max(0, Number(prixClientLocal) || 0),
                estimated_delivery_days: data.estimated_delivery_days,
                created_at: new Date().toISOString() });
      setOpen(false);
      if (data.label_url) window.open(data.label_url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/stallion/track/${stallion.tracking_number}`);
      onSaved({ ...stallion, status: data.status });
      toast.success(`Statut du colis : ${data.status || "inconnu"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  if (!cfg) {
    // Chargement du statut : on montre un repère visible plutôt que « rien » —
    // demande du propriétaire : le bouton Stallion ne doit JAMAIS disparaître.
    return (
      <span className="inline-flex items-center gap-1.5 text-xs rounded-full border border-ivory/15 text-ivory/50 px-3 py-1.5"
        data-testid={`stallion-loading-${docId}`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Stallion…
      </span>
    );
  }

  // Non configuré ou statut illisible : AVERTISSEMENT VISIBLE avec la raison et
  // un bouton « Revérifier » (avant : bouton gris pâle quasi invisible = « disparu »)
  if (!cfg.configured) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-testid={`stallion-not-ready-${docId}`}>
        <span className="inline-flex items-center gap-1.5 text-xs rounded-full border border-amber-400/60 text-amber-300 px-3 py-1.5"
          title={cfg.reason || "Statut Stallion indisponible"}>
          <Truck className="w-3.5 h-3.5" /> Stallion non configuré
        </span>
        <span className="text-[11px] text-amber-300/80">{cfg.reason}</span>
        <button type="button" data-testid={`stallion-retry-${docId}`}
          onClick={() => { setCfg(null); api.get("/admin/stallion/status").then(({ data }) => setCfg(data)).catch(() => setCfg({ configured: false, reason: "Statut Stallion indisponible" })); }}
          className="text-xs text-gold hover:text-champagne underline underline-offset-2 transition-colors">
          Revérifier
        </button>
      </span>
    );
  }

  // Étiquette déjà créée : suivi + réimpression + rafraîchissement du statut
  if (stallion?.tracking_number) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-testid={`stallion-tracking-${docId}`}>
        <span className="text-[10px] uppercase tracking-widest border border-emerald-500/40 text-emerald-300 rounded-full px-2.5 py-1"
          title={`Service ${stallion.service || "—"}`}>
          Stallion · {stallion.tracking_number} · {stallion.status || "créée"}
          {/* Coûtant RÉEL visible côté admin (demande du propriétaire) — jamais au client */}
          {stallion.cost != null && stallion.cost !== "" && (
            <strong data-testid={`stallion-cost-${docId}`} className="text-champagne"> · coût {Number(stallion.cost).toFixed(2)} $</strong>
          )}
          {/* Estimation de livraison Stallion (demande du propriétaire 2026-10-10) */}
          {stallion.estimated_delivery_days != null && (
            <em data-testid={`stallion-estimate-${docId}`} className="text-sky-300 normal-case"
              title="Délai annoncé par Stallion au moment de l'achat de l'étiquette">
              {" "}· ≈ {stallion.estimated_delivery_days} j
              {stallion.created_at ? ` (vers le ${fmtDateMtl(new Date(new Date(stallion.created_at).getTime() + stallion.estimated_delivery_days * 86400000).toISOString())})` : ""}
            </em>
          )}
        </span>
        {stallion.label_file || stallion.label_url ? (
          <a href={stallion.label_file || stallion.label_url} target="_blank" rel="noreferrer"
            data-testid={`stallion-label-${docId}`}
            className="text-xs text-gold hover:underline underline-offset-2">Étiquette PDF</a>
        ) : null}
        <button type="button" onClick={refresh} disabled={busy} data-testid={`stallion-refresh-${docId}`}
          title="Rafraîchir le statut du colis maintenant"
          className="text-ivory/60 hover:text-gold transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
      </span>
    );
  }

  return (
    <>
      <button type="button" onClick={begin} disabled={!cfg.configured} data-testid={`stallion-create-${docId}`}
        title={cfg.configured ? "Créer l'étiquette d'expédition Stallion (achat réel sur votre compte Stallion)" : cfg.reason}
        className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-3 py-1.5 transition-colors duration-300 ${
          cfg.configured
            ? "border-gold/40 text-gold hover:bg-gold hover:text-ink"
            : "border-ivory/10 text-ivory/30 cursor-not-allowed"}`}>
        <Truck className="w-3.5 h-3.5" /> Étiquette Stallion
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-gold/20 text-ivory w-[calc(100vw-1.5rem)] max-w-lg max-h-[85dvh] overflow-y-auto overscroll-contain p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Étiquette d'expédition Stallion</DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">
              Vérifiez l'adresse et le colis — la création achète l'étiquette sur votre compte Stallion ({cfg.env}).
            </DialogDescription>
          </DialogHeader>
          {f && (
            <div className="space-y-3" data-testid="stallion-form">
              {/* Popup compacte (demande du propriétaire — le bas restait invisible) :
                  champs sur 2 colonnes + bouton d'achat COLLANT toujours visible */}
              <div className="grid sm:grid-cols-2 gap-3">
              {[["name", "Nom du destinataire"], ["address1", "Adresse"], ["address2", "Adresse (suite)"],
                ["city", "Ville"], ["postal_code", "Code postal"], ["phone", "Téléphone"], ["email", "Courriel"]].map(([k, lab]) => (
                <label key={k} className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/55 mb-1">{lab}</span>
                  <input value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))}
                    data-testid={`stallion-f-${k}`}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                </label>
              ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/55 mb-1">Province</span>
                  <input value={f.province_code} maxLength={2} onChange={(e) => setF((x) => ({ ...x, province_code: e.target.value.toUpperCase() }))}
                    data-testid="stallion-f-province"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/55 mb-1">Poids (lb)</span>
                  <input type="number" min="0.1" step="0.1" value={f.weight} onChange={(e) => setF((x) => ({ ...x, weight: e.target.value }))}
                    data-testid="stallion-f-weight"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[["length", "Long. (po)"], ["width", "Larg. (po)"], ["height", "Haut. (po)"]].map(([k, lab]) => (
                  <label key={k} className="block">
                    <span className="block text-[10px] uppercase tracking-widest text-ivory/55 mb-1">{lab}</span>
                    <input type="number" min="1" step="0.5" value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))}
                      data-testid={`stallion-f-${k}`}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
                  </label>
                ))}
              </div>
              <div className="sticky bottom-0 bg-surface -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-3 border-t border-ivory/10">
                {/* Estimation SANS ACHAT (demande du propriétaire 2026-10-10) :
                    prix + délai affichés avant de créer l'étiquette */}
                {est?.chargement && (
                  <p className="text-[11px] text-ivory/55 mb-2 flex items-center gap-1.5" data-testid="stallion-estimating">
                    <Loader2 className="w-3 h-3 animate-spin" /> Estimation du tarif et du délai…
                  </p>
                )}
                {est && !est.chargement && est.ok === false && (
                  <p className="text-[11px] mb-2 rounded-lg border border-amber-400/30 bg-amber-400/5 px-3 py-2 text-amber-200"
                    data-testid="stallion-estimate-unavailable">
                    {est.raison || "Estimation indisponible pour le moment"}
                  </p>
                )}
                {est && !est.chargement && est.ok !== false && est.total != null && (
                  <p className="text-[11px] mb-2 rounded-lg border border-sky-400/30 bg-sky-400/5 px-3 py-2 text-sky-200"
                    data-testid="stallion-estimate">
                    {est.service ? <strong>{est.service}</strong> : "Service le moins cher"} — ≈ {Number(est.total).toFixed(2)} $
                    {est.estimated_delivery_days != null && <> — livraison estimée : <strong>≈ {est.estimated_delivery_days} jour{est.estimated_delivery_days > 1 ? "s" : ""}</strong> (vers le {fmtDateMtl(new Date(Date.now() + est.estimated_delivery_days * 86400000).toISOString())})</>}
                  </p>
                )}
                {f.address1 && f.city && f.postal_code && !est?.chargement && (
                  <button type="button" onClick={() => estimer(f)} data-testid="stallion-reestimate"
                    className="block text-[10px] text-ivory/50 hover:text-gold underline underline-offset-2 mb-2 transition-colors">
                    Réestimer avec ces champs
                  </button>
                )}
                {/* Coût réel Stallion vs prix facturé au client, marge en direct
                    (demande du propriétaire 2026-10-10) */}
                <div className="grid grid-cols-3 gap-2 mb-3 items-end" data-testid="stallion-margin">
                  <div>
                    <span className="block text-[10px] uppercase tracking-widest text-ivory/55 mb-1">Coût Stallion</span>
                    <p className="text-sm text-ivory/85 py-2" data-testid="stallion-margin-cost">
                      {est && !est.chargement && est.ok !== false && est.total != null
                        ? `≈ ${Number(est.total).toFixed(2)} $`
                        : <span className="text-ivory/40">à l'achat</span>}
                    </p>
                  </div>
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-widest text-ivory/55 mb-1">Livraison client ($)</span>
                    <input type="number" min="0" step="0.01" value={prixClientLocal}
                      onChange={(e) => setPrixClientLocal(e.target.value)}
                      disabled={!onPrixClient} data-testid="stallion-prix-client"
                      title={onPrixClient ? "Prix de livraison facturé au client — ajustable avant l'achat" : "Déjà encaissé — non modifiable"}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none disabled:opacity-50" />
                    {!onPrixClient && <span className="text-[9px] text-ivory/40">déjà encaissé</span>}
                  </label>
                  <div>
                    <span className="block text-[10px] uppercase tracking-widest text-ivory/55 mb-1">Marge</span>
                    {(() => {
                      const cout = est && !est.chargement && est.ok !== false && est.total != null ? Number(est.total) : null;
                      const pv = prixClientLocal.trim() === "" ? null : Number(prixClientLocal);
                      if (cout == null || pv == null || isNaN(pv)) {
                        return <p className="text-sm text-ivory/40 py-2" data-testid="stallion-margin-value">—</p>;
                      }
                      const m = pv - cout;
                      return (
                        <p className={`text-sm font-bold py-2 ${m >= 0 ? "text-emerald-300" : "text-red-300"}`}
                          data-testid="stallion-margin-value">
                          {m >= 0 ? "+" : ""}{m.toFixed(2)} $
                        </p>
                      );
                    })()}
                  </div>
                </div>
                <button onClick={create} disabled={busy || !f.name || !f.address1 || !f.city || !f.postal_code}
                  data-testid="stallion-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold rounded-full px-5 py-3 text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Créer et acheter l'étiquette
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
